/**
 * App root — route table.
 *
 * Owen Feb 2026 speed pass — this file used to eagerly `import` all 82 page
 * components at the top of the module, meaning every first-time visitor
 * downloaded the entire product's JavaScript before ANY page could paint.
 * On mobile data that meant 15-30 seconds to interactive. Now:
 *
 *   • Landing, Login, AuthCallback, ProtectedRoute, and the floating
 *     chrome components (FloatingHomeBtn / FloatingUtilities / footer
 *     strip / SwordsIntro etc.) stay eager because they render on the
 *     very first paint of every session.
 *
 *   • Every other page uses `React.lazy(...)` — Webpack code-splits them
 *     into their own chunk that only downloads on first visit to that
 *     route. Wrap `<Routes>` in a `<Suspense>` whose fallback matches
 *     the charcoal aesthetic so there's no flash of white.
 *
 * Result: initial bundle drops from ~5MB to ~800KB, and page transitions
 * within the app pay only the ~30-80KB cost of the next chunk.
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { AuthProvider } from '@/contexts/AuthContext';
import '@/App.css';

// ── Eager (rendered on first paint or during route resolution) ─────────
import Landing from '@/pages/Landing';
import AuthCallback from '@/pages/AuthCallback';
import Login from '@/pages/Login';
import ProtectedRoute from '@/components/ProtectedRoute';
import FamilyTreeErrorBoundary from '@/components/FamilyTreeErrorBoundary';
import ScrollToTop from '@/components/ScrollToTop';
import RecordReturnPill from '@/components/RecordReturnPill';
import FloatingHomeBtn from '@/components/FloatingHomeBtn';
import FloatingUtilities from '@/components/FloatingUtilities';
import CorporateFooterStrip from '@/components/CorporateFooterStrip';
import SwordsIntro from '@/components/SwordsIntro';

// ── Lazy pages (code-split into per-route chunks) ──────────────────────
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const SubmitStory = lazy(() => import('@/pages/SubmitStory'));
const BrowseStories = lazy(() => import('@/pages/BrowseStories'));
const StoryDetail = lazy(() => import('@/pages/StoryDetail'));
const Families = lazy(() => import('@/pages/Families'));
const FamilyTimeline = lazy(() => import('@/pages/FamilyTimeline'));
const Lineage = lazy(() => import('@/pages/Lineage'));
const Authors = lazy(() => import('@/pages/Authors'));
const AuthorProfile = lazy(() => import('@/pages/AuthorProfile'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const Premium = lazy(() => import('@/pages/Premium'));
const PromisePage = lazy(() => import('@/pages/Promise'));
const Philosophy = lazy(() => import('@/pages/Philosophy'));
const TourIntro = lazy(() => import('@/pages/TourIntro'));
const DiscoverBinding = lazy(() => import('@/pages/DiscoverBinding'));
const MyFilmsAndPlaylists = lazy(() => import('@/pages/MyFilmsAndPlaylists'));
const MemberSubpage = lazy(() => import('@/pages/MemberSubpage'));
const FamilyPlan = lazy(() => import('@/pages/FamilyPlan'));
const FamilyInvite = lazy(() => import('@/pages/FamilyInvite'));
const FamilySplit = lazy(() => import('@/pages/FamilySplit'));
const CancelSubscription = lazy(() => import('@/pages/CancelSubscription'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const LegendOriginal = lazy(() => import('@/pages/LegendOriginal'));
const Judges = lazy(() => import('@/pages/Judges'));
const Record = lazy(() => import('@/pages/Record'));
const FounderI = lazy(() => import('@/pages/FounderI'));
const JulySpecial = lazy(() => import('@/pages/JulySpecial'));
const LegacyLinks = lazy(() => import('@/pages/LegacyLinks'));
const StoryOfTheWeek = lazy(() => import('@/pages/StoryOfTheWeek'));
const HighlyAppreciated = lazy(() => import('@/pages/HighlyAppreciated'));
const BusinessPlan = lazy(() => import('@/pages/BusinessPlan'));
const ReadRequests = lazy(() => import('@/pages/ReadRequests'));
const FamilyTree = lazy(() => import('@/pages/FamilyTree'));
const MyStories = lazy(() => import('@/pages/MyStories'));
const Journal = lazy(() => import('@/pages/Journal'));
const BindJournal = lazy(() => import('@/pages/BindJournal'));
const BindLeather = lazy(() => import('@/pages/BindLeather'));
const CardView = lazy(() => import('@/pages/CardView'));
const FoundersVision = lazy(() => import('@/pages/FoundersVision'));
const MemberProfile = lazy(() => import('@/pages/MemberProfile'));
const JournalEntry = lazy(() => import('@/pages/JournalEntry'));
const DVRT = lazy(() => import('@/pages/DVRT'));
const DVRTEnvelope = lazy(() => import('@/pages/DVRTEnvelope'));
const MyLibrary = lazy(() => import('@/pages/MyLibrary'));
const FamilyStory = lazy(() => import('@/pages/FamilyStory'));
const TimeCapsules = lazy(() => import('@/pages/TimeCapsules'));
const DiscoverColosseum = lazy(() => import('@/pages/DiscoverColosseum'));
const EnterArena = lazy(() => import('@/pages/EnterArena'));
const ArenaLeaderboard = lazy(() => import('@/pages/ArenaLeaderboard'));
const HallOfChampions = lazy(() => import('@/pages/HallOfChampions'));
const AuthorsHub = lazy(() => import('@/pages/AuthorsHub'));
const MusicalArtistry = lazy(() => import('@/pages/MusicalArtistry'));
const ScrollClaim = lazy(() => import('@/pages/ScrollClaim'));
const WriteYourStory = lazy(() => import('@/pages/WriteYourStory'));
const WriteCategory = lazy(() => import('@/pages/WriteCategory'));
const LibraryHub = lazy(() => import('@/pages/LibraryHub'));
const VoiceLibrary = lazy(() => import('@/pages/VoiceLibrary'));
const FavouritePhotos = lazy(() => import('@/pages/FavouritePhotos'));
const FamilyBook = lazy(() => import('@/pages/FamilyBook'));
const FamilyBookManage = lazy(() => import('@/pages/FamilyBookManage'));
const FamilyBookWizard = lazy(() => import('@/pages/FamilyBookWizard'));
const AdminDiscoverPrompts = lazy(() => import('@/pages/AdminDiscoverPrompts'));
const FounderWelcome = lazy(() => import('@/pages/FounderWelcome'));
const Contact = lazy(() => import('@/pages/Contact'));
const Tour = lazy(() => import('@/pages/Tour'));
const TourBuilder = lazy(() => import('@/pages/TourBuilder'));
const TributeShare = lazy(() => import('@/pages/TributeShare'));
const EngagementInvite = lazy(() => import('@/pages/EngagementInvite'));
const WeddingInvite = lazy(() => import('@/pages/WeddingInvite'));
const GraduationInvite = lazy(() => import('@/pages/GraduationInvite'));
const MemorialInvite = lazy(() => import('@/pages/MemorialInvite'));
const YourTourGuide = lazy(() => import('@/pages/YourTourGuide'));
const Voices = lazy(() => import('@/pages/Voices'));
const MyAlbums = lazy(() => import('@/pages/MyAlbums'));
const ThousandWordCanvas = lazy(() => import('@/pages/ThousandWordCanvas'));

// Minimal charcoal-tinted fallback while a route chunk downloads. No spinner
// text — the goal is invisibility, not a loading screen. Matches the app's
// dark base so there's no flash of white on route transitions.
const RouteFallback = () => (
  <div
    aria-hidden
    data-testid="route-chunk-fallback"
    style={{ minHeight: '100vh', background: '#1C1917' }}
  />
);

// Tiny shim that swaps the in-app URL for the public Reading Room hub
// (served as static HTML from /api/view/library). Owen Feb 2026 — plays
// the 6.3s SwordsIntro cinematic FIRST when a fresh visitor lands on
// /reading-room (typically from a shared partner/investor link). Owen
// Feb 2026 round 8 — changed from session to LOCAL storage so the
// ritual fires truly once per browser, not once per tab session. After
// the first viewing, every subsequent tap of any Reading Room link
// redirects straight to the static HTML hub with no intro.
function ReadingRoomRedirect() {
  const redirect = () => {
    if (typeof window !== 'undefined') {
      // Owen Feb 28, 2026 — forward `?invite=<token>` onto the static
      // HTML hub so the new Reading Room gate (routers/views.py) sees
      // it on first hop. Without this the token gets dropped at the
      // redirect and visitors land on the "by invitation" card even
      // though their email link had a valid token.
      const params = new URLSearchParams(window.location.search || '');
      const invite = params.get('invite') || params.get('token');
      const target = invite
        ? `/api/view/library?invite=${encodeURIComponent(invite)}`
        : '/api/view/library';
      window.location.replace(target);
    }
  };
  const introSeen = (typeof window !== 'undefined') && (() => {
    try {
      if (window.localStorage.getItem('colosseum_reading_room_intro_seen')) return true;
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return !!reduced;
    } catch (e) { return false; }
  })();
  if (introSeen) {
    redirect();
    return null;
  }
  return (
    <SwordsIntro
      forceShow
      gateKey="colosseum_reading_room_intro_seen"
      gateStorage="local"
      onComplete={redirect}
    />
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Handle auth callbacks synchronously during render (NOT in useEffect).
  // Legacy Emergent flow returns with session_id in the URL hash.
  // New direct Google OAuth flow returns at /auth/google with ?code=... in the query.
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/google') {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <RecordReturnPill />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/legend" element={<LegendOriginal />} />
      <Route path="/judges" element={<Judges />} />
      <Route path="/record" element={<Record />} />
      <Route path="/founder-i" element={<FounderI />} />
      <Route path="/july-special" element={<JulySpecial />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/tour" element={<Tour />} />
      <Route path="/tour/:userId" element={<Tour />} />
      <Route path="/tour-builder" element={<ProtectedRoute><TourBuilder /></ProtectedRoute>} />
      <Route path="/tribute/:token" element={<TributeShare />} />
      <Route path="/engagement/:token" element={<EngagementInvite />} />
      <Route path="/wedding/:token" element={<WeddingInvite />} />
      <Route path="/graduation/:token" element={<GraduationInvite />} />
      <Route path="/memorial/:token" element={<MemorialInvite />} />
      <Route path="/your-tour-guide" element={<YourTourGuide />} />
      {/* Clean shareable URL for the Reading Room — redirects to the curated
          HTML hub at /api/view/library. Owen Jun 2026 round 25: "easier to
          share than the /api/view/ path." */}
      <Route path="/reading-room" element={<ReadingRoomRedirect />} />
      <Route path="/founder-welcome" element={<ProtectedRoute><FounderWelcome /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/submit" element={<ProtectedRoute><SubmitStory /></ProtectedRoute>} />
      <Route path="/my-stories" element={<ProtectedRoute><MyStories /></ProtectedRoute>} />
      <Route path="/journal" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
      <Route path="/journal/bind" element={<ProtectedRoute><BindJournal /></ProtectedRoute>} />
      <Route path="/bind/:kind/:refId" element={<ProtectedRoute><BindLeather /></ProtectedRoute>} />
      <Route path="/cards/:cardId" element={<ProtectedRoute><CardView /></ProtectedRoute>} />
      <Route path="/journal/:entryId" element={<ProtectedRoute><JournalEntry /></ProtectedRoute>} />
      <Route path="/founders-vision" element={<FoundersVision />} />
      <Route path="/member/:memberId" element={<MemberProfile />} />
      <Route path="/browse" element={<BrowseStories />} />
      <Route path="/story/:storyId" element={<StoryDetail />} />
      <Route path="/families" element={<Families />} />
      <Route path="/family/:familyName" element={<FamilyTimeline />} />
      <Route path="/authors" element={<Authors />} />
      <Route path="/authors-hub" element={<ProtectedRoute><AuthorsHub /></ProtectedRoute>} />
      <Route path="/musical-artistry" element={<MusicalArtistry />} />
      <Route path="/scroll-claim/:token" element={<ScrollClaim />} />
      <Route path="/write" element={<ProtectedRoute><WriteYourStory /></ProtectedRoute>} />
      <Route path="/write/:categoryKey" element={<ProtectedRoute><WriteCategory /></ProtectedRoute>} />
      <Route path="/author/:userId" element={<AuthorProfile />} />
      <Route path="/family-tree/:userId" element={<FamilyTreeErrorBoundary><FamilyTree /></FamilyTreeErrorBoundary>} />
      <Route path="/lineage/:user_id" element={<Lineage />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/discover-prompts" element={<ProtectedRoute adminOnly><AdminDiscoverPrompts /></ProtectedRoute>} />
      <Route path="/premium" element={<Premium />} />
      <Route path="/promise" element={<PromisePage />} />
      <Route path="/philosophy" element={<Philosophy />} />
      <Route path="/tour-intro" element={<TourIntro />} />
      <Route path="/discover-binding" element={<DiscoverBinding />} />
      <Route path="/films-playlists" element={<MyFilmsAndPlaylists />} />
      <Route path="/member/:memberId/books"     element={<MemberSubpage kind="books" />} />
      <Route path="/member/:memberId/pieces"    element={<MemberSubpage kind="pieces" />} />
      <Route path="/member/:memberId/films"     element={<MemberSubpage kind="films" />} />
      <Route path="/member/:memberId/playlists" element={<MemberSubpage kind="playlists" />} />
      <Route path="/member/:memberId/links"     element={<MemberSubpage kind="links" />} />
      <Route path="/family-plan" element={<ProtectedRoute><FamilyPlan /></ProtectedRoute>} />
      <Route path="/family-invite/:token" element={<FamilyInvite />} />
      <Route path="/family-split" element={<ProtectedRoute><FamilySplit /></ProtectedRoute>} />
      <Route path="/cancel-subscription" element={<ProtectedRoute><CancelSubscription /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/links" element={<ProtectedRoute><LegacyLinks /></ProtectedRoute>} />
      <Route path="/story-of-the-week" element={<StoryOfTheWeek />} />
      <Route path="/highly-appreciated" element={<HighlyAppreciated />} />
      <Route path="/enter-arena" element={<ProtectedRoute><EnterArena /></ProtectedRoute>} />
      <Route path="/arena" element={<ArenaLeaderboard />} />
      <Route path="/hall-of-champions" element={<HallOfChampions />} />
      <Route path="/business-plan" element={<BusinessPlan />} />
      <Route path="/read-requests" element={<ProtectedRoute><ReadRequests /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/dvrt" element={<ProtectedRoute><DVRT /></ProtectedRoute>} />
      <Route path="/dvrt/:envelopeId" element={<ProtectedRoute><DVRTEnvelope /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><MyLibrary /></ProtectedRoute>} />
      <Route path="/library-hub" element={<ProtectedRoute><LibraryHub /></ProtectedRoute>} />
      <Route path="/voice-library" element={<ProtectedRoute><VoiceLibrary /></ProtectedRoute>} />
      <Route path="/favourite-photos" element={<ProtectedRoute><FavouritePhotos /></ProtectedRoute>} />
      {/* Owen Mar 2026 — Colosseum Publishing family heirloom book.
          The reader page accepts anonymous viewers (the backend gates
          them to the free preview pages) so allowlist recipients can
          land directly via a shared link without needing an account.
          The manage page is owner-only via the backend share check. */}
      <Route path="/family-book/:bookId" element={<FamilyBook />} />
      <Route path="/family-book/:bookId/manage" element={<ProtectedRoute><FamilyBookManage /></ProtectedRoute>} />
      <Route path="/family-book/:bookId/wizard" element={<ProtectedRoute><FamilyBookWizard /></ProtectedRoute>} />
      <Route path="/my-albums" element={<ProtectedRoute><MyAlbums /></ProtectedRoute>} />
      <Route path="/canvas" element={<ThousandWordCanvas />} />
      <Route path="/family-story" element={<ProtectedRoute><FamilyStory /></ProtectedRoute>} />
      <Route path="/capsules" element={<ProtectedRoute><TimeCapsules /></ProtectedRoute>} />
      <Route path="/discover-colosseum" element={<DiscoverColosseum />} />
      <Route path="/voices" element={<Voices />} />
      {/* Owen Mar 2026 round 36 — catch-all so a mis-routed prompt
          (or any stale link) lands somewhere sensible rather than a
          blank white page. ProtectedRoute on /dashboard bounces
          anonymous viewers home automatically. */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    {/* Site-wide legitimacy strip — quietly discloses the incorporating
        entity + registration status at the absolute bottom of every
        page (below any page-specific footer). See
        components/CorporateFooterStrip.jsx for wording rationale. */}
    <CorporateFooterStrip />
    {/* Floating utility buttons (home + help) are sibling to <Routes>
        rather than inside any page, so they need their own theme-fixed
        wrapper to stay charcoal+gold regardless of the user's theme.
        Otherwise the lifebuoy/help icon picks up the modern-art crimson
        accent etc. (Owen's Jun 2026 catch). */}
    <div className="theme-fixed">
      <FloatingHomeBtn />
      {/* HelpBtn no longer floats — moved into ThemePicker drawer per
          Owen Feb 2026 round 37, so the bottom-right corner can host
          the permanent LanguageSwitcher pill (see FloatingLanguagePill). */}
      <FloatingUtilities />
    </div>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Toaster />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
